import { TimeSeriesDistribution, TimeResolution, DataFilter } from "interfaces";

import {
  PeriodicityType,
  FourierType,
  SeasonalityModel,
  TimeSeriesModelInterface,
  TimeSeriesQuality,
  TimeSeriesChangePointsData,
  TimeSeriesAnomaliesData,
  TimeSeriesComponentsData,
  TimeSeriesClusters,
  TimeSeriesAnomaliesMultiVariate,
} from "interfaces";

import { useGetDataInsight } from "./insights";

interface TimeSeriesDistributionsSpec {
  time_column: string;
  resolution?: TimeResolution;
  value_column: string;
  value_column_type: "numeric" | "char" | "date";
  grouping_columns: string[];
  filters?: DataFilter.Filter[];
}

export const useGetTimeSeriesDistribution = (
  fileId: string,
  spec: TimeSeriesDistributionsSpec
) => {
  return useGetDataInsight<TimeSeriesDistribution | "Too little data!!!">({
    fileId,
    name: "data_time_series_distributions",
    params: spec,
  });
};

interface TimeSeriesQualitySpec {
  time_column: string;
  grouping_columns: string[];
  filters?: DataFilter.Filter[];
}

export const useGetTimeSeriesQuality = (
  fileId: string,
  spec: TimeSeriesQualitySpec
) => {
  return useGetDataInsight<TimeSeriesQuality>({
    fileId,
    name: "data_time_series_quality",
    params: spec,
  });
};

interface TimeSeriesChangePointsSpec {
  time_column: string;
  resolution: TimeResolution;
  value_column: string;
  adjust_for_seasonality: boolean;
  min_regime_length: number;
  grouping_columns: string[];
  filters: DataFilter.Filter[];
}

export const useGetTimeSeriesChangePoints = (
  fileId: string,
  spec: TimeSeriesChangePointsSpec
) => {
  return useGetDataInsight<TimeSeriesChangePointsData>({
    fileId,
    name: "data_time_series_change_points",
    params: spec,
  });
};

interface TimeSeriesModelSpec {
  time_column: string;
  resolution?: TimeResolution;
  value_column: string;
  grouping_columns?: string[];
  filters: DataFilter.Filter[];
  adjust_for_seasonality: boolean;
  max_lag: number;
  min_regime_length?: number;
}

export const useGetTimeSeriesAnomalies = (
  fileId: string,
  spec: TimeSeriesModelSpec
) => {
  return useGetDataInsight<TimeSeriesAnomaliesData>({
    fileId,
    name: "data_time_series_anomalies",
    params: spec,
  });
};

interface TimeSeriesModelMultipleColumnSpec {
  time_column: string;
  resolution?: TimeResolution;
  value_columns: string[];
  grouping_columns: string[];
  filters: DataFilter.Filter[];
  adjust_for_seasonality: boolean;
  max_lag?: number;
  min_regime_length?: number;
  max_lag_by_column?: { [column: string]: number };
}

export const useGetTimeSeriesAnomaliesMultipleColumns = (
  fileId: string,
  spec: TimeSeriesModelMultipleColumnSpec
) => {
  return useGetDataInsight<{ [col: string]: TimeSeriesAnomaliesData }>({
    fileId,
    name: "data_time_series_anomalies",
    params: spec,
    enabled: spec.value_columns.length >= 1,
  });
};

export const useGetTimeSeriesMultivariateAnomaliesModel = (
  fileId: string,
  spec: Omit<TimeSeriesModelMultipleColumnSpec, "value_columns">
) => {
  return useGetDataInsight<TimeSeriesAnomaliesMultiVariate>({
    fileId,
    name: "data_time_series_anomalies_multivariate",
    params: spec,
  });
};

interface TS_Model {
  shape: [number, number];
  columns: FourierType[];
  model: TimeSeriesModelInterface;
  mdl: number;
  "sigma-2": number;
  lag: number;
  periodicities: Exclude<PeriodicityType, "__INTERCEPT__">[];
}

interface TimeSeriesModelData {
  models: { [group: string]: TS_Model };
  "key-group-mapper": { [group: string]: string[] };
}

export const useGetTimeSeriesModels = (
  fileId: string,
  spec: TimeSeriesModelSpec
) => {
  return useGetDataInsight<TimeSeriesModelData>({
    fileId,
    name: "data_time_series_models",
    params: spec,
  });
};

export const useGetTimeSeriesModelsMultipleColumns = (
  fileId: string,
  spec: TimeSeriesModelMultipleColumnSpec
) => {
  return useGetDataInsight<{ [col: string]: TimeSeriesModelData }>({
    fileId,
    name: "data_time_series_models",
    params: spec,
  });
};

interface TimeSeriesComponentsSpec {
  time_column: string;
  resolution?: TimeResolution;
  value_column: string;
  grouping_columns: string[];
  filters: DataFilter.Filter[];
}

export const useGetTimeSeriesComponents = (
  fileId: string,
  spec: TimeSeriesComponentsSpec
) => {
  return useGetDataInsight<TimeSeriesComponentsData[]>({
    fileId,
    name: "data_time_series_components",
    params: spec,
  });
};

interface ResidualsForGroup {
  group: string[] | null;
  model: SeasonalityModel;
  values: { time: string[]; value: number[] };
}

export const useGetTimeSeriesBaseline = (
  fileId: string,
  spec: TimeSeriesComponentsSpec
) =>
  useGetDataInsight<ResidualsForGroup[]>({
    fileId,
    name: "data_time_series_baseline",
    params: spec,
  });

interface TimeSeriesRelationships {
  group: string[] | null;
  corr: {
    [col: string]: {
      "raw-x-raw-y": number;
      "raw-x-deb-y": number;
      "deb-x-raw-y": number;
      "deb-x-deb-y": number;
    };
  };
}

export const useGetTimeSeriesRelationships = (
  fileId: string,
  spec: TimeSeriesComponentsSpec
) =>
  useGetDataInsight<TimeSeriesRelationships[]>({
    fileId,
    name: "data_time_series_relationships",
    params: spec,
  });

interface TimeSeriesClustersSpec {
  time_column: string;
  resolution?: TimeResolution;
  cluster_columns: string[];
  label_columns: string[];
  filters?: DataFilter.Filter[];
}

export const useGetTimeSeriesClusters = (
  fileId: string,
  spec: TimeSeriesClustersSpec
) =>
  useGetDataInsight<{ [col: string]: TimeSeriesClusters[] }>({
    fileId,
    name: "data_time_series_clusters",
    params: spec,
    enabled: spec.label_columns.length >= 1 && spec.cluster_columns.length >= 1,
  });
